import { useEffect } from "react";
import { reportLovableError } from "@/lib/lovable-error-reporting";

/**
 * Installs window-level listeners for uncaught errors and unhandled promise
 * rejections and forwards them to the Lovable monitoring hook so runtime
 * failures outside React render (event handlers, async code, third-party
 * scripts) are still captured.
 */
export function GlobalErrorListeners() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onError = (event: ErrorEvent) => {
      reportLovableError(event.error ?? new Error(event.message), {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }, { mechanism: "onerror", handled: false });
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      reportLovableError(event.reason ?? new Error("Unhandled promise rejection"), {
        type: "unhandledrejection",
      }, { mechanism: "unhandledrejection", handled: false });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
